import { ConfirmDialog } from '@/components/confirm-modal/confirm-dialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useDownloadStore } from '@/stores/downloadStore'
import { formatBytes, formatBytesPerSecond } from '@/utils/formatBytes'
import { ProgressCircle } from '@/utils/streamingDownloader'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Trash2, X, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

function formatETAFriendly(seconds: number): string {
	if (seconds === Number.POSITIVE_INFINITY || Number.isNaN(seconds)) {
		return 'Calculating...'
	}
	if (seconds < 60) {
		return `${Math.round(seconds)} sec`
	}
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = Math.round(seconds % 60)
	if (remainingSeconds === 0) {
		return `${minutes} min`
	}
	return `${minutes} min ${remainingSeconds} sec`
}

export function DownloadManager() {
	const { downloads, isMinimized, setMinimized, removeDownload, clearCompleted, cancelDownload } = useDownloadStore()
	const [cancelTargetId, setCancelTargetId] = useState<string | null>(null)

	const activeDownloads = downloads.filter(d => d.status === 'preparing' || d.status === 'downloading')
	const hasActive = activeDownloads.length > 0

	useEffect(() => {
		if (downloads.length > 0 && !hasActive) {
			const timer = setTimeout(() => {
				clearCompleted()
			}, 3000)
			return () => clearTimeout(timer)
		}
	}, [downloads.length, hasActive, clearCompleted])

	if (downloads.length === 0) {
		return null
	}

	const handleCancelConfirm = () => {
		if (cancelTargetId) {
			cancelDownload(cancelTargetId)
			setCancelTargetId(null)
		}
	}

	const cancelTarget = downloads.find(d => d.id === cancelTargetId)

	return (
		<>
			{/* Above the dialog layer (z-100) and clickable while a modal dialog holds body pointer-events:none.
			    The pointerdown stop keeps a click here from dismissing that dialog. */}
			<div
				className={`fixed bottom-4 right-4 z-110 w-80 pointer-events-auto bg-popover shadow-levitating border border-border rounded-md overflow-hidden flex flex-col transition-all duration-300 ${
					isMinimized ? 'h-10' : 'max-h-96 h-auto'
				}`}
				onPointerDown={event => event.stopPropagation()}
				data-testid='download-manager'>
				<div className='h-10 px-3 bg-muted/20 border-b border-border flex items-center justify-between shrink-0 select-none'>
					<span className='text-foreground text-control font-semibold'>
						{hasActive ? `Downloading (${activeDownloads.length} active)` : 'Downloads complete'}
					</span>
					<div className='flex items-center gap-1'>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant='ghost'
									size='icon'
									className='size-control text-muted-foreground hover:text-foreground'
									onClick={() => setMinimized(!isMinimized)}
									aria-label={isMinimized ? 'Expand downloads' : 'Minimize downloads'}
									data-testid='download-manager-toggle'>
									{isMinimized ? (
										<ChevronUp className='size-(--icon-standalone)' aria-hidden='true' />
									) : (
										<ChevronDown className='size-(--icon-standalone)' aria-hidden='true' />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>{isMinimized ? 'Expand downloads' : 'Minimize downloads'}</p>
							</TooltipContent>
						</Tooltip>
						{!hasActive && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant='ghost'
										size='icon'
										className='size-control text-muted-foreground hover:text-foreground'
										onClick={() => clearCompleted()}
										aria-label='Clear completed downloads'
										data-testid='download-manager-clear'>
										<X className='size-(--icon-standalone)' aria-hidden='true' />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>Clear completed downloads</p>
								</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>

				{!isMinimized && (
					<div className='flex-1 overflow-y-auto p-2 space-y-2'>
						{downloads.map(item => {
							const downloadedStr = formatBytes(item.bytesReceived)
							const totalStr = formatBytes(item.totalBytes)
							const sizeStr = `${downloadedStr} / ${totalStr}`
							// A chunked response carries no Content-Length, so there is no percentage to show.
							const hasTotal = item.totalBytes > 0
							const showsPercent = hasTotal && item.showProgress !== false

							const metaText =
								item.status === 'preparing'
									? 'Preparing...'
									: item.status === 'downloading'
										? showsPercent
											? `${sizeStr} • ${formatBytesPerSecond(item.speed)} • ${formatETAFriendly(item.eta)}`
											: hasTotal
												? `${sizeStr} • ${formatBytesPerSecond(item.speed)}`
												: `${downloadedStr} • ${formatBytesPerSecond(item.speed)}`
										: item.status === 'completed'
											? 'Complete'
											: item.status === 'cancelled'
												? 'Cancelled'
												: item.error || 'Failed'

							return (
								<div
									key={item.id}
									className='p-2 border border-border bg-card rounded-md flex items-center gap-2'
									data-testid={`download-manager-item-${item.id}`}>
									<div className='shrink-0'>
										{item.status === 'completed' ? (
											<CheckCircle2 className='size-5 text-success' />
										) : item.status === 'failed' ? (
											<AlertCircle className='size-5 text-destructive' />
										) : item.status === 'cancelled' ? (
											<XCircle className='size-5 text-muted-foreground' />
										) : hasTotal ? (
											<ProgressCircle percent={item.percent} />
										) : (
											<Spinner className='size-5' />
										)}
									</div>

									<div className='flex-1 min-w-0'>
										<p className='text-foreground text-control font-semibold truncate select-none text-left'>
											{item.filename}
											{item.status === 'downloading' && showsPercent && ` (${item.percent}%)`}
										</p>
										<p className='text-muted-foreground text-control font-normal truncate select-none text-left'>
											{metaText}
										</p>
									</div>

									<div className='shrink-0'>
										{item.status === 'preparing' || item.status === 'downloading' ? (
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant='ghost'
														size='icon'
														className='size-control text-muted-foreground hover:text-destructive'
														onClick={() => setCancelTargetId(item.id)}
														aria-label={`Cancel download of ${item.filename}`}
														data-testid={`download-manager-cancel-${item.id}`}>
														<X className='size-(--icon-standalone)' aria-hidden='true' />
													</Button>
												</TooltipTrigger>
												<TooltipContent>
													<p>Cancel download</p>
												</TooltipContent>
											</Tooltip>
										) : (
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant='ghost'
														size='icon'
														className='size-control text-muted-foreground hover:text-foreground'
														onClick={() => removeDownload(item.id)}
														aria-label={`Remove download of ${item.filename}`}
														data-testid={`download-manager-remove-${item.id}`}>
														<Trash2 className='size-(--icon-standalone)' aria-hidden='true' />
													</Button>
												</TooltipTrigger>
												<TooltipContent>
													<p>Remove download</p>
												</TooltipContent>
											</Tooltip>
										)}
									</div>
								</div>
							)
						})}
					</div>
				)}
			</div>

			<ConfirmDialog
				open={cancelTargetId !== null}
				onOpenChange={open => !open && setCancelTargetId(null)}
				title='Cancel Download?'
				description={`Are you sure you want to cancel the download of '${cancelTarget?.filename}'?`}
				confirmLabel='Cancel Download'
				cancelLabel='Keep Downloading'
				confirmVariant='destructive'
				onConfirm={handleCancelConfirm}
			/>
		</>
	)
}
